import { FunctionComponent, useEffect, useState } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'

import { fetch_all, fetch_one } from '../../lib/server_helper';
import { checkAllowed } from '../../lib/permission';

import {
  CircularProgress,
  Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { GridColDef } from '@mui/x-data-grid-pro';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import { getCookie, setCookie } from 'react-use-cookie';

import {
  DataGrid,
  Page,
  SearchBox,
  StyledButton,
  StyledIconButton,
  Typography
} from '../../components/interface';

import { Report } from '../../models/Report';
import { loginAtom } from '../../lib/auth';

type Props = {
  adminAsOrganiser?: boolean;
};

export const AdminReportsCompany: FunctionComponent<Props> = ({adminAsOrganiser}) => {
  // const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  const [login, setLogin] = useAtom(loginAtom);

  const params = useParams();
  const projectId: string | undefined = params.project_id;

  const [objects, setObjects] = useState<Report[]>([]);
  const [objectsCount, setObjectsCount] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(true);
  const [removeLoader, setRemoveLoader] = useState<boolean>(false);
  const [pullLoader, setPullLoader] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("id DESC");
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);
  const [activeCompany, setActiveCompany] = useState<string>(getCookie('panelmetrics_active_company_id') || '');

  // Settings


  useEffect(() => {
    if (!!activeCompany) onSearch('');
  }, [page, order, activeCompany]);

  const onCopy = (id: number) => {
    // if (checkAllowed(login, null, projectId?.toString() || '0', "ProjectReports", 'd', true)) {
      setPullLoader(true);
      fetch_one<Report>('/nl/v3/objects/custom_action', {
        object: 'report',
        class_action: 'custom_api_copy_report',
        id: id
      }, (obs, complete_data) => {
        onSearch('');
        setPullLoader(false);
      }, (z) => {
        
      }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
    // }
  };

  const onRemove = (id: number) => {
    // if (checkAllowed(login, null, projectId?.toString() || '0', "ProjectReports", 'd', true)) {
      setRemoveLoader(true);
      fetch_one<Report>('/nl/v3/objects/remove', {
        object: 'report',
        ob: {
          id: id
        }
      }, (obs, complete_data) => {
        onSearch('');
        setRemoveLoader(false);
      }, (z) => {

      }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
    // }
  };

  const onPull = (id: number) => {
    // if (checkAllowed(login, null, projectId?.toString() || '0', "ProjectReports", 'd', true)) {
      setPullLoader(true);
      fetch_one<Report>('/nl/v3/objects/custom_action', {
        object: 'report',
        class_action: 'custom_api_pull_latest',
        id: id
      }, (obs, complete_data) => {
        onSearch('');
        setPullLoader(false);
      }, (z) => {

      }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
    // }
  };

  const onSearch = (s: string) => {
    // if (checkAllowed(login, null, projectId?.toString() || '0', "ProjectReports", 'r', true)) {
      // console.log(filters);
      setLoader(true);
      fetch_all<Report>('/nl/v3/objects/fetch_all', {
        object: 'report',
        fields: ['id', 'created_at', 'updated_at', 'name', 'report_versions|first|id/name/get_item_xls', 'report_versions|first|user|id/full_name', 'project_id'],
        per_page: 20,
        page: page,
        order: order,
        filter: {
          search: s,
          advanced: {
            company_id: activeCompany,
            fallback: s,
            is_simplified: !!login?.is_organiser || !!adminAsOrganiser
          }
        }
      }, (obs, complete_data) => {
        setObjects(obs);

        let count:number = 0;
        if (typeof(complete_data?.data) === 'object' && 'count' in complete_data?.data) count = complete_data?.data?.count;
        setObjectsCount(count);

        setLoader(false);
      }, (z) => {
        setServerErrorSnackOpen(true);
      }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
    // } else {
    //   setLoader(false);
    // }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 100, sortable: true },
    { field: 'name', headerName: 'Name', width: 250, sortable: true },
    { field: 'report_versions_first_name', headerName: 'Latest Version', width: 250, sortable: false, renderCell: params => <>
      {!!params.row.report_versions_first_id && <Typography sx={{
        fontSize: '0.9rem',
        cursor: 'pointer',
        textDecoration: 'underline'
      }} onClick={() => {
        navigate(`/report_version/${params.row.report_versions_first_id}`);
      }}>{params.row.report_versions_first_name}</Typography>}
      {!!params.row.report_versions_first_id && login?.is_admin && <StyledIconButton path={`/report_versions/${params.row.id}`} sx={{marginLeft: 1}}>
        <UnfoldMoreIcon />
      </StyledIconButton>}
    </> },
    { field: 'report_versions_first_user_full_name', headerName: 'Latest Version by', width: 250, sortable: false, renderCell: params => <>
      {!!params.row.report_versions_first_id && <Typography sx={{
        fontSize: '0.9rem'
      }}>{params.row.report_versions_first_user_full_name}</Typography>}
    </> },
    ...(!(!login?.is_admin && !!login?.is_organiser) ? [{ field: 'raw_xls', headerName: 'XLSX', width: 100, sortable: false, renderCell: (params:any) => <>
      {!!params.row.report_versions_first_get_item_xls && <CloudDownloadIcon onClick={() => {
        window.open(params.row.report_versions_first_get_item_xls, "_BLANK");
      }} sx={{
        cursor: 'pointer',
      }} />}
    </> }] : []),
  ];

  return (
    <Page sx={{}} title={t("objects.reports.title", "Rapporten")} actions={<>
      {/* {checkAllowed(login, null, projectId?.toString() || '0', "ProjectReports", 'c', true) && <StyledButton
        label="Create new"
        id='create_new'
        contained
        path={`/report/${projectId}/new`}
        sx={{marginRight: 3}}
        startIcon={<AddCircleOutlineIcon />}
      />} */}
      {!!login?.is_admin && <StyledButton
        label="Create new"
        id='create_new'
        contained
        path={`/report/${projectId}/new`}
        sx={{marginRight: 3}}
        startIcon={<AddCircleOutlineIcon />}
      />}
    </>}>
      <SearchBox
        simple={true}
        onSearch={(s) => {
          setPage(0);
          onSearch(s);
        }}
      />
      <DataGrid
        action_count={!!login?.is_admin ? 4 : 1}
        actions={(params) => {
          return (<>
            {/* {checkAllowed(login, null, projectId?.toString() || '0', "ProjectReports", 'u', true) && <StyledIconButton title="Edit" color="primary" path={`/report/${projectId}/${params.row.id}`}>
              <EditIcon />
            </StyledIconButton>} */}
            {!!login?.is_admin && <StyledIconButton title="Copy" color="primary" onClick={() => {
              onCopy(params.row.id);
            }}>
              <ContentCopyIcon />
            </StyledIconButton>}
            {!!login?.is_admin && <StyledIconButton title="Edit" color="primary" path={`/report/${params.row.project_id}/${params.row.id}`}>
              <EditIcon />
            </StyledIconButton>}
            {!!removeLoader && <CircularProgress />}
            {/* {checkAllowed(login, null, projectId?.toString() || '0', "ProjectReports", 'd', true) && !removeLoader && <StyledIconButton title="Destroy" destroyBtn color="primary" onClick={(e) => {
              onRemove(params.row.id);
            }}>
              <DeleteForeverIcon />
            </StyledIconButton>} */}
            {!!login?.is_admin && !removeLoader && <StyledIconButton title="Destroy" destroyBtn color="primary" onClick={(e) => {
              onRemove(params.row.id);
            }}>
              <DeleteForeverIcon />
            </StyledIconButton>}
            {!pullLoader && <StyledIconButton title="Pull latest" color="primary" onClick={(e) => {
              onPull(params.row.id);
            }}>
              <NoteAddIcon />
            </StyledIconButton>}
          </>);
        }}
        getRowClassName={(params) => `datagrid-row-enabled-${params.row.enabled}`}
        objectCount={objectsCount}
        objects={(objects || []).map(u => ({
          ...u
        }))}
        columns={columns}
        loader={loader}
        onSortModelChange={(a, b) => {
          let a_0_field:string = a[0].field;
          setOrder([a_0_field, a[0].sort].join(' '));
        }}
        page={page}
        setPage={(p) => {setPage(p);}}
      />

      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
      {!!open && <Snackbar
      open={open}
        onClose={() => {setOpen(false);}}
        autoHideDuration={2000}
      >
        <MuiAlert elevation={6} variant="filled" severity="success">Copied!</MuiAlert>
      </Snackbar>}
    </Page>
  );
}
